import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import {
  INSTITUTION_ORIGINS,
  PORTAL_BRAND,
  institutionFromUser,
  isAllowedOnThisPortal,
  redirectToInstitutionPortal,
} from '../config/institution';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login({ email, password });
      const user = result.user;

      // Cross-portal: ANR credentials on CNS login → redirect to ANR
      if (redirectToInstitutionPortal(result.token, user)) {
        return;
      }

      if (!isAllowedOnThisPortal(user)) {
        authService.logout();
        const inst = institutionFromUser(user);
        if (inst === 'anr') {
          setError(`Compte ANR detecte. Utilisez le portail ANR (${INSTITUTION_ORIGINS.anr}).`);
        } else {
          setError('Acces refuse. Ce portail est reserve aux comptes CNS.');
        }
        return;
      }

      navigate('/');
      window.location.reload();
    } catch (err) {
      // Display user-friendly error messages
      const errorMessage = err instanceof Error ? err.message : 'Login failed. Please try again.';
      
      // Map specific error messages to user-friendly ones
      if (errorMessage.includes('Invalid email or password')) {
        setError('Invalid email or password. Please check your credentials.');
      } else if (errorMessage.includes('Account temporarily locked')) {
        setError(errorMessage); // Show the exact message with time remaining
      } else if (errorMessage.includes('Account is inactive')) {
        setError('Your account is inactive. Please contact support.');
      } else if (errorMessage.includes('Access denied')) {
        setError('Access denied. Insufficient permissions.');
      } else if (errorMessage.includes('temporarily unavailable')) {
        setError('Authentication service is temporarily unavailable. Please try again later.');
      } else {
        setError(errorMessage);
      }
      
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-blue-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-3 bg-white rounded-full mb-4">
            <svg className="w-12 h-12 text-blue-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white">{PORTAL_BRAND.fullName}</h1>
          <p className="text-blue-200 mt-2">{PORTAL_BRAND.subtitle}</p>
          <p className="text-blue-100/80 text-xs mt-3">
            Connexion partagee CNS / ANR — redirection automatique selon votre institution
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Officer Sign In</h2>

          {error && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              error.includes('locked') 
                ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              <div className="flex items-start">
                {error.includes('locked') ? (
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                )}
                <span>{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Officer Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder="officer@cns.gov"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-900 text-white py-2 px-4 rounded-lg hover:bg-blue-800 focus:ring-4 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Authenticating...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Security Notice */}
          <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-xs text-yellow-800">
              <strong>Security Notice:</strong> This system is for authorized personnel only. All activities are monitored and logged.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-blue-200">
          <p>© 2025 National Security Service - Classified System</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
